<template>
  <div class="bind-editor">
    <div class="tags-container">
      <div
        class="custom-tag"
        :class="{ 'is-open': activeSelect.index === index }"
        v-for="(item, index) in internalItems"
        :key="`bind-${index}-${item.key}`"
      >
        <span
          v-if="!(editingKey.index === index)"
          class="tag-label"
          :title="`Chave: ${item.key}`"
          @dblclick="startEditKey(index)"
        >
          {{ labelFor(item) }}
        </span>

        <input
          v-else
          v-model="editingKey.draft"
          class="tag-key-input"
          type="text"
          :disabled="item.fixed"
          :title="`Será salvo como: ${normalizeFieldKey(editingKey.draft)}`"
          @keyup.enter="commitEditKey(index)"
          @blur="commitEditKey(index)"
          @keyup.esc="cancelEditKey"
          v-focus
        />

        <div v-if="activeSelect.index === index" class="tag-select-wrapper">
          <b-form-input
            v-model="internalItems[index].value"
            placeholder="Busque ou digite um valor"
            @input="onSelectInput(index)"
            @keydown="handleSelectKeydown($event, index)"
            @blur="clearActiveSelect"
            v-focus
            class="tag-select"
          ></b-form-input>

          <div class="tag-options-list">
            <button
              v-for="(v, optionIndex) in filteredOptions(
                internalItems[index].value
              )"
              :key="`opt-${index}-${v}`"
              type="button"
              class="tag-option-item"
              :class="{ active: optionIndex === highlightedOptionIndex }"
              @mousedown.prevent="selectOption(index, v)"
            >
              {{ v }}
            </button>

            <div
              v-if="!filteredOptions(internalItems[index].value).length"
              class="tag-option-empty"
            >
              Nenhuma opção encontrada. O texto digitado será mantido.
            </div>
          </div>
        </div>

        <div
          v-else
          class="tag-value"
          :class="{ 'has-value': item.value }"
          @click="setActiveSelect(index)"
        >
          {{ formatValueDisplay(item.value) }}
        </div>

        <div v-if="!item.fixed" class="tag-remove" @click="removeItem(index)">
          <i class="fas fa-times"></i>
        </div>
      </div>

      <div class="add-field-wrapper">
        <b-button
          v-if="!addingField"
          size="sm"
          class="btn-add-tag"
          @click="addingField = true"
        >
          <i class="fas fa-plus"></i> Adicionar Campo
        </b-button>

        <b-form-input
          v-else
          size="sm"
          v-model="newFieldName"
          placeholder="Nome da chave"
          :title="`Será salvo como: ${normalizeFieldKey(newFieldName)}`"
          @keyup.enter="commitNewField"
          @blur="commitNewField"
          v-focus
          class="input-add-tag"
        ></b-form-input>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BindKeyValueEditor",
  directives: {
    focus: {
      inserted(el) {
        if (el && typeof el.focus === "function") {
          el.focus();
        }
      }
    }
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    selectOptions: {
      type: Array,
      default: () => []
    },
    fixedItems: {
      type: Array,
      default: () => []
    },
    fixedLabelMap: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      internalItems: [],
      syncFromParentInProgress: false,
      lastParentSnapshot: "",
      activeSelect: { index: null },
      highlightedOptionIndex: 0,
      editingKey: { index: null, draft: "" },
      addingField: false,
      newFieldName: ""
    };
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler() {
        this.syncFromProps();
      }
    },
    fixedItems: {
      deep: true,
      immediate: true,
      handler() {
        this.syncFromProps();
      }
    },
    internalItems: {
      deep: true,
      handler() {
        if (this.syncFromParentInProgress) return;
        this.$emit("input", this.internalItems);
        this.$emit("change", this.internalItems);
      }
    }
  },
  methods: {
    snapshotItems(items) {
      return JSON.stringify(
        (items || []).map((item) => ({
          key: item.key,
          value: item.value,
          fixed: !!item.fixed
        }))
      );
    },
    normalizeFieldKey(value) {
      if (!value) return "";
      return value
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/^_+|_+$/g, "");
    },
    unwrapVariable(value) {
      const str = (value || "").toString().trim();
      const match = str.match(/^\{\s*(.+?)\s*\}$/);
      return match ? match[1] : str;
    },
    wrapVariable(value) {
      const token = this.unwrapVariable(value);
      return token ? `{${token}}` : "";
    },
    mapOption(value) {
      return typeof value === "string"
        ? value
        : (value && (value.value || value.text)) || "";
    },
    normalizedOptions() {
      return (this.selectOptions || []).map(this.mapOption).filter((v) => !!v);
    },
    normalizedFixedItems() {
      return (this.fixedItems || [])
        .map((item) => {
          if (typeof item === "string") {
            return {
              key: this.normalizeFieldKey(item),
              value: "",
              fixed: true
            };
          }
          return {
            key: this.normalizeFieldKey(item && item.key),
            value: (item && item.value) || "",
            fixed: item ? item.fixed !== false : true
          };
        })
        .filter((item) => !!item.key);
    },
    syncFromProps() {
      const current = (this.value || [])
        .map((item) => ({
          key: this.normalizeFieldKey(item && item.key),
          value: (item && item.value) || "",
          fixed: !!(item && item.fixed)
        }))
        .filter((item) => !!item.key);

      const fixed = this.normalizedFixedItems();
      const fixedMap = new Map(fixed.map((f) => [f.key, f]));

      const merged = current.map((item) => {
        if (fixedMap.has(item.key)) {
          return { ...item, fixed: true };
        }
        return item;
      });

      fixed.forEach((f) => {
        const foundIndex = merged.findIndex((m) => m.key === f.key);
        if (foundIndex === -1) {
          merged.unshift({ ...f });
        }
      });

      const nextSnapshot = this.snapshotItems(merged);
      const currentSnapshot = this.snapshotItems(this.internalItems);
      if (
        nextSnapshot === currentSnapshot ||
        nextSnapshot === this.lastParentSnapshot
      ) {
        this.lastParentSnapshot = nextSnapshot;
        return;
      }

      this.syncFromParentInProgress = true;
      this.internalItems = merged;
      this.lastParentSnapshot = nextSnapshot;
      this.$nextTick(() => {
        this.syncFromParentInProgress = false;
      });
    },
    labelFor(item) {
      return this.fixedLabelMap[item.key] || item.key;
    },
    isVariableOption(value) {
      if (!value) return false;
      return this.normalizedOptions().includes(this.unwrapVariable(value));
    },
    formatValueDisplay(value) {
      if (!value) return "Vincular...";
      if (this.isVariableOption(value)) return this.wrapVariable(value);
      return value;
    },
    filteredOptions(search) {
      const termo = this.unwrapVariable(search).toLowerCase().trim();
      const options = this.normalizedOptions();
      if (!termo) return options;
      return options.filter((v) => v.toLowerCase().includes(termo));
    },
    setActiveSelect(index) {
      this.activeSelect = { index };
      const currentItem = this.internalItems && this.internalItems[index];
      const currentValue = (currentItem && currentItem.value) || "";
      const options = this.filteredOptions(currentValue);
      const token = this.unwrapVariable(currentValue);
      const optionIndex = options.findIndex((v) => v === token);
      this.highlightedOptionIndex = optionIndex > -1 ? optionIndex : 0;
    },
    clearActiveSelect() {
      this.activeSelect = { index: null };
      this.highlightedOptionIndex = 0;
    },
    onSelectInput(index) {
      const currentItem = this.internalItems && this.internalItems[index];
      const currentValue = (currentItem && currentItem.value) || "";
      const options = this.filteredOptions(currentValue);
      const token = this.unwrapVariable(currentValue);
      const optionIndex = options.findIndex((v) => v === token);
      this.highlightedOptionIndex = optionIndex > -1 ? optionIndex : 0;
    },
    selectOption(index, value) {
      this.$set(this.internalItems, index, {
        ...this.internalItems[index],
        value: this.wrapVariable(value)
      });
      this.clearActiveSelect();
    },
    handleSelectKeydown(event, index) {
      const currentItem = this.internalItems && this.internalItems[index];
      const currentValue = (currentItem && currentItem.value) || "";
      const options = this.filteredOptions(currentValue);

      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (!options.length) return;
        this.highlightedOptionIndex =
          (this.highlightedOptionIndex + 1) % options.length;
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (!options.length) return;
        this.highlightedOptionIndex =
          (this.highlightedOptionIndex - 1 + options.length) % options.length;
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        const typedValue = (currentValue || "").toString().trim();

        if (options.length) {
          const safeIndex = Math.min(
            Math.max(this.highlightedOptionIndex, 0),
            options.length - 1
          );
          this.selectOption(index, options[safeIndex]);
          return;
        }

        if (typedValue) {
          this.$set(this.internalItems, index, {
            ...this.internalItems[index],
            value: typedValue
          });
          this.clearActiveSelect();
          return;
        }

        this.clearActiveSelect();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        this.clearActiveSelect();
      }
    },
    startEditKey(index) {
      if (this.internalItems[index] && this.internalItems[index].fixed) return;
      this.editingKey = {
        index,
        draft: this.internalItems[index].key
      };
    },
    cancelEditKey() {
      this.editingKey = { index: null, draft: "" };
    },
    commitEditKey(index) {
      const current = this.internalItems[index];
      if (!current || current.fixed) {
        this.cancelEditKey();
        return;
      }

      const newKey = this.normalizeFieldKey(this.editingKey.draft);
      if (!newKey || newKey === current.key) {
        this.cancelEditKey();
        return;
      }

      if (
        this.internalItems.some(
          (item, idx) => idx !== index && item.key === newKey
        )
      ) {
        this.cancelEditKey();
        return;
      }

      this.$set(this.internalItems, index, {
        ...current,
        key: newKey
      });
      this.cancelEditKey();
    },
    removeItem(index) {
      if (this.internalItems[index] && this.internalItems[index].fixed) return;
      this.internalItems.splice(index, 1);
    },
    commitNewField() {
      const key = this.normalizeFieldKey(this.newFieldName);
      if (key && !this.internalItems.some((item) => item.key === key)) {
        this.internalItems.push({ key, value: "", fixed: false });
        this.$nextTick(() => {
          this.setActiveSelect(this.internalItems.length - 1);
        });
      }
      this.addingField = false;
      this.newFieldName = "";
    }
  }
};
</script>

<style scoped>
.bind-editor {
  width: 100%;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.custom-tag {
  display: inline-flex;
  align-items: stretch;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  overflow: hidden;
  font-size: 0.875rem;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
}

.custom-tag.is-open {
  overflow: visible;
  z-index: 20;
}

.tag-label {
  padding: 0.35rem 0.5rem;
  background-color: #f1f5f9;
  color: #475569;
  font-weight: 500;
  border-right: 1px solid #cbd5e1;
  display: flex;
  align-items: center;
}

.tag-value {
  padding: 0.35rem 0.5rem;
  cursor: pointer;
  min-width: 90px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-style: italic;
  font-size: 0.75rem;
  transition: background-color 0.2s;
}

.tag-value.has-value {
  color: #472282;
  background-color: #f4f0fa;
  font-style: normal;
  font-weight: 600;
  font-family: monospace;
  font-size: 0.8rem;
}

.tag-value:hover {
  background-color: #f8fafc;
}

.tag-value.has-value:hover {
  background-color: #e9e3f5;
}

.tag-select {
  border: none !important;
  outline: none !important;
  background-color: transparent !important;
  color: #472282 !important;
  min-width: 240px;
  padding: 0.45rem 0.65rem !important;
  font-size: 0.8rem !important;
}

.tag-select-wrapper {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  background-color: #ffffff;
  min-width: 240px;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  box-shadow: 0 12px 28px rgba(27, 39, 94, 0.15);
  z-index: 30;
}

.tag-options-list {
  max-height: 180px;
  overflow-y: auto;
  border-top: 1px solid #d9cfee;
  background-color: #fff;
}

.tag-option-item {
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  color: #4c327a;
  padding: 0.45rem 0.65rem;
  font-size: 0.8rem;
  cursor: pointer;
}

.tag-option-item:hover {
  background-color: #f4f0fa;
}

.tag-option-item.active {
  background-color: #ede5fb;
  color: #472282;
}

.tag-option-empty {
  color: #7b6a9e;
  font-size: 0.75rem;
  padding: 0.45rem 0.65rem;
}

.tag-remove {
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  border-left: 1px solid #cbd5e1;
  cursor: pointer;
}

.tag-remove:hover {
  color: #ef4444;
  background-color: #fef2f2;
}

.add-field-wrapper {
  display: inline-flex;
}

.btn-add-tag {
  background-color: #fff !important;
  color: #64748b !important;
  border: 1px dashed #cbd5e1 !important;
  font-size: 0.875rem !important;
  box-shadow: none !important;
}

.btn-add-tag:hover {
  border-color: #472282 !important;
  color: #472282 !important;
  background-color: #f4f0fa !important;
}

.input-add-tag {
  width: 140px !important;
  font-size: 0.875rem !important;
  height: auto !important;
  padding: 0.25rem 0.5rem !important;
}

.tag-key-input {
  border: none;
  outline: none;
  width: 120px;
  padding: 0.35rem 0.5rem;
  color: #472282;
  background-color: #f4f0fa;
  border-right: 1px solid #cbd5e1;
}
</style>
